import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideo,
  faDesktop,
  faCirclePlay,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import InviteTab from "../Invite/InviteTab";
import Profiles from "../Invite/Profiles";
import CallHistory from "./CallHistory";
import VisitorList from "../VisitorList/VisitorList";
import zafClient from "../../zafclient";
import storage from "../../hooks/storage";
import { TenantConfigContext } from "../../store/tenantconfig-context";
import "./CallHistory.css";

const MODES = { VIDEO: "video", COBROWSE: "cobrowse" };
const SUBTABS = { SESSIONS: "sessions", HISTORY: "history" };
const MODE_KEY = "ui_mode";

// `zcli apps:server` delivers checkbox params as the strings "true"/"false"
// (CLI prompt input), while an installed Zendesk app delivers real booleans.
// Coerce both shapes so local dev matches production behavior.
const asBool = (v, fallback) => {
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;
  return fallback;
};

// Client-approved Option 1: top-level Mode tabs (Video Support / Cobrowse)
// with Sessions / History sub-pills inside Video mode. Cobrowse currently
// has no History (BE support pending) so it renders just the Connect UI
// with no pills. Persist the last selected mode so an agent who mainly
// does Cobrowse doesn't have to re-select it every ticket. Subtab is NOT
// persisted — matches prior ShowMe behavior (always land on Sessions /
// the compose view). The `default_to_cobrowse` admin flag layers on top
// of the mode fallback here.
const MainTabs = () => {
  const { teamid } = useParams();
  // CallHistory exposes { refresh } via useImperativeHandle so re-clicking
  // the active History pill (in Video mode) refreshes the list — same
  // affordance the old two-tab layout had, no separate refresh button.
  const historyRef = useRef(null);

  // Tenant-level Cobrowse Add-On entitlement (Blitzz Super Admin flag),
  // orthogonal to the Zendesk-side `enable_cobrowse` app setting. Fetched
  // once per auth/refresh in tenantconfig-context; while it's in flight
  // (`isLoading`) we render nothing inside the Cobrowse panels so we don't
  // flash the live UI and then swap it for the permission message.
  const tenantCfg = useContext(TenantConfigContext);

  // `null` = still resolving admin settings; render nothing until known so
  // we don't flash the wrong tab / a disabled tab before metadata arrives.
  const [settings, setSettings] = useState(null);
  const [mode, setMode] = useState(null);
  const [subtab, setSubtab] = useState(SUBTABS.SESSIONS);

  useEffect(() => {
    let cancelled = false;
    zafClient
      .metadata()
      .then((meta) => {
        if (cancelled) return;
        const s = (meta && meta.settings) || {};
        const enableVideo = asBool(s.enable_video_support, true);
        const enableCobrowse = asBool(s.enable_cobrowse, true);
        // default_to_cobrowse is meaningful only when Cobrowse is enabled
        const defaultToCobrowse =
          enableCobrowse && asBool(s.default_to_cobrowse, false);

        setSettings({ enableVideo, enableCobrowse, defaultToCobrowse });

        if (!enableVideo && !enableCobrowse) {
          setMode(null);
          return;
        }
        if (enableVideo && !enableCobrowse) {
          setMode(MODES.VIDEO);
          return;
        }
        if (!enableVideo && enableCobrowse) {
          setMode(MODES.COBROWSE);
          return;
        }

        // Both enabled — honor last valid selection, else admin default,
        // else Video Support.
        const saved = storage.get(MODE_KEY);
        if (saved === MODES.VIDEO || saved === MODES.COBROWSE) {
          setMode(saved);
        } else if (defaultToCobrowse) {
          setMode(MODES.COBROWSE);
        } else {
          setMode(MODES.VIDEO);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // If metadata fails, fall back to the pre-toggle behavior: both
        // modes visible, last saved mode wins, else Video Support.
        setSettings({
          enableVideo: true,
          enableCobrowse: true,
          defaultToCobrowse: false,
        });
        const saved = storage.get(MODE_KEY);
        setMode(
          saved === MODES.COBROWSE || saved === MODES.VIDEO
            ? saved
            : MODES.VIDEO
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only persist the mode when the user *explicitly* clicks a mode tab —
  // never on the programmatic initial landing. Otherwise a user who never
  // touched the tabs still ends up with a saved value, and a later change
  // to the admin's `default_to_cobrowse` flag would silently be ignored on
  // their machine forever.
  const selectMode = (next) => {
    storage.set(MODE_KEY, next);
    setMode(next);
  };

  const onSubtabClick = (target) => {
    if (subtab === target) {
      // Re-clicking the active History pill refreshes. Pills only render
      // for Video mode, so the mode check is defensive.
      if (
        mode === MODES.VIDEO &&
        target === SUBTABS.HISTORY &&
        historyRef.current &&
        historyRef.current.refresh
      ) {
        historyRef.current.refresh();
      }
    } else {
      setSubtab(target);
    }
  };

  const historyPillTitle =
    mode === MODES.VIDEO && subtab === SUBTABS.HISTORY
      ? "Click to refresh"
      : "History";

  if (!settings) {
    // Metadata not yet resolved — avoid flashing a possibly-disabled tab.
    return null;
  }

  if (!settings.enableVideo && !settings.enableCobrowse) {
    return (
      <div className="ch-list">
        <div className="ch-empty">
          <div className="ch-empty-title">Blitzz isn't set up yet</div>
          <div className="ch-empty-sub">
            Ask your Zendesk administrator to turn on Video Support or
            Cobrowse for your workspace to get started.
          </div>
        </div>
      </div>
    );
  }

  const bothEnabled = settings.enableVideo && settings.enableCobrowse;

  // Cobrowse mode selected but the tenant lacks the Add-On entitlement.
  // In this state we hide the Sessions / History sub-pills entirely and
  // show a single centered message (Sessions/History don't apply when the
  // feature itself is unavailable). Video mode is unaffected.
  const cobrowseGated =
    mode === MODES.COBROWSE &&
    !tenantCfg.isLoading &&
    !tenantCfg.hasCobrowseAddOn;

  // Sub-pills only apply to Video mode. Cobrowse History isn't implemented
  // yet, so the Cobrowse mode shows just the Connect-Using-Code UI
  // (VisitorList) with no Sessions/History pill row above it. Re-enable the
  // pills for Cobrowse when History lands.
  const showSubpills = mode === MODES.VIDEO;

  return (
    <div>
      {bothEnabled && (
        <div className="blitzz-tabs" role="tablist">
          {settings.enableVideo && (
            <button
              type="button"
              role="tab"
              aria-selected={mode === MODES.VIDEO}
              className={
                "blitzz-tab" + (mode === MODES.VIDEO ? " active" : "")
              }
              onClick={() => selectMode(MODES.VIDEO)}
            >
              <FontAwesomeIcon icon={faVideo} /> Video Support
            </button>
          )}
          {settings.enableCobrowse && (
            <button
              type="button"
              role="tab"
              aria-selected={mode === MODES.COBROWSE}
              className={
                "blitzz-tab" + (mode === MODES.COBROWSE ? " active" : "")
              }
              onClick={() => selectMode(MODES.COBROWSE)}
            >
              <FontAwesomeIcon icon={faDesktop} /> Cobrowse
            </button>
          )}
        </div>
      )}

      {showSubpills && (
        <div
          className={
            "blitzz-subpills" +
            (bothEnabled ? "" : " blitzz-subpills--top")
          }
          role="tablist"
        >
          <button
            type="button"
            role="tab"
            aria-selected={subtab === SUBTABS.SESSIONS}
            className={
              "blitzz-subpill" + (subtab === SUBTABS.SESSIONS ? " active" : "")
            }
            onClick={() => onSubtabClick(SUBTABS.SESSIONS)}
          >
            <FontAwesomeIcon icon={faCirclePlay} /> Sessions
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={subtab === SUBTABS.HISTORY}
            className={
              "blitzz-subpill" + (subtab === SUBTABS.HISTORY ? " active" : "")
            }
            onClick={() => onSubtabClick(SUBTABS.HISTORY)}
            title={historyPillTitle}
          >
            <FontAwesomeIcon icon={faClockRotateLeft} /> History
          </button>
        </div>
      )}

      {/*
        Panels are always mounted (per-enabled-mode) and hidden with the
        HTML `hidden` attribute rather than conditionally rendered — the
        standard WAI-ARIA tabpanel pattern. This preserves component state
        across tab switches: VisitorList's SignalR socket stays open and
        its device list survives navigating to Video Support and back
        (previously the list emptied because unmount closed the socket and
        device announcements are one-shot pushes). CallHistory keeps its
        loaded sessions and scroll position for the same reason.
        Panels for a disabled mode are not mounted at all so no socket or
        history fetch happens when the admin has turned that mode off.
      */}
      <div className="blitzz-tabpanes">
        {settings.enableVideo && (
          <>
            <div
              role="tabpanel"
              hidden={
                !(mode === MODES.VIDEO && subtab === SUBTABS.SESSIONS)
              }
            >
              {teamid ? <InviteTab /> : <Profiles />}
            </div>
            <div
              role="tabpanel"
              hidden={
                !(mode === MODES.VIDEO && subtab === SUBTABS.HISTORY)
              }
            >
              <CallHistory
                ref={historyRef}
                active={mode === MODES.VIDEO && subtab === SUBTABS.HISTORY}
              />
            </div>
          </>
        )}
        {settings.enableCobrowse && tenantCfg.hasCobrowseAddOn && (
          <div
            role="tabpanel"
            hidden={!(mode === MODES.COBROWSE)}
          >
            <VisitorList />
          </div>
        )}
        {settings.enableCobrowse && cobrowseGated && (
          <div role="tabpanel">
            <div className="ch-list">
              <div className="ch-empty">
                <div className="ch-empty-title">Cobrowse Add-On required</div>
                <div className="ch-empty-sub">
                  To use Cobrowse in Zendesk, your Blitzz account needs the
                  Cobrowse Add-On.{" "}
                  <a
                    href="https://blitzz.co/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contact Blitzz
                  </a>{" "}
                  to activate it.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainTabs;
